import React, { useEffect, useState } from 'react';
import AssetActions from '../../actions/AssetActions';
import ViewerActions from '../../actions/ViewerActions';
import FacilityStore from '../../stores/FacilityStore';
import ViewerStore from '../../stores/ViewerStore';
import KnowledgeBookStore from '../../stores/KnowledgeBookStore';
import ViewerHelpers from '../../helpers/ViewerHelpers';
import { getViewMediaKind, isBlackAndWhiteScanPosition } from '../../helpers/viewMedia';
import FacilityAssetSetStore from '../../stores/FacilityAssetSetStore';


declare var InovxLoader: any;
/* ------------------ COMPONENT ------------------ */
interface ButtonProps {
    positionsOnly?: boolean;
    assetSets?: IAssetSet[];
}

const facilityHas360Images = () => {
    return !!FacilityStore.getSelectedFacility()?.Views?.some((view) => getViewMediaKind(view) === 'image360');
};

const facilityHasColorScanPositions = () => {
    return !!FacilityStore.getSelectedFacility()?.Views?.some((view) =>
        getViewMediaKind(view) === 'scanPositions' && !isBlackAndWhiteScanPosition(view));
};

const facilityHasBlackAndWhiteScanPositions = () => {
    return !!FacilityStore.getSelectedFacility()?.Views?.some((view) =>
        getViewMediaKind(view) === 'scanPositions' && isBlackAndWhiteScanPosition(view));
};

const getAssetTagClassOptions = (assetSets: IAssetSet[] = KnowledgeBookStore.getAllAssetSet()) => {
    const facility = FacilityStore.getSelectedFacility();
    const optionsById = new Map<string, { id: string; name: string }>();
    const isPanoViewer = ViewerStore.isPanoViewerVisible();
    const selectedOntologyID = FacilityStore.getSelectedOntology()?.ID;
    const filteredAssetIDs = new Set(ViewerStore.getFilteredAssets() ?? []);
    const selectedKnowledgeView = ViewerStore.getSelectedKnowledgeView();
    const knowledgeViewResolution = selectedKnowledgeView
        ? ViewerHelpers.resolveKnowledgeViewAssetTags(
            selectedKnowledgeView.AssetSets,
            assetSets,
            (facility?.AssetTags ?? []).map(tag => tag.ID)
        )
        : null;
    const visibleKnowledgeViewTagIDs = new Set(knowledgeViewResolution?.visibleTagIDs ?? []);
    const claimedKnowledgeViewTagIDs = new Set([
        ...(knowledgeViewResolution?.visibleTagIDs ?? []),
        ...(knowledgeViewResolution?.hiddenTagIDs ?? []),
    ]);

    (facility?.AssetTags ?? []).forEach(tag => {
        if (!ViewerHelpers.isPlacedAssetTag(tag)) return;
        if (!isPanoViewer && selectedOntologyID && !AssetActions.matchesTagSelectedOntology(tag, selectedOntologyID)) return;
        if (tag.AssetID && filteredAssetIDs.has(tag.AssetID)) return;
        if (selectedKnowledgeView && !visibleKnowledgeViewTagIDs.has(tag.ID) &&
            !(knowledgeViewResolution?.fallbackVisible && !claimedKnowledgeViewTagIDs.has(tag.ID))) return;

        const id = ViewerHelpers.getAssetTagClassID(tag, facility);
        if (!id) return;
        if (optionsById.has(id)) return;

        optionsById.set(id, {
            id,
            name: tag.AssetClass?.Name ?? facility?.Assets?.find(asset => asset.ID === tag.AssetID)?.AssetClass?.Name ?? facility?.AssetClasses?.find(assetClass => assetClass.ID === id)?.Name ?? InovxLoader.t('facility:queryBuilder.assetClass')
        });
    });

    return Array.from(optionsById.values()).sort((left, right) => left.name.localeCompare(right.name));
};

const ContentLayersButton: React.FC<ButtonProps> = ({ positionsOnly = false, assetSets }) => {
    const [selectedOntologyTypes, setSelectedOntologyTypes] = useState<string[]>(FacilityStore.getSelectedOntologyPartContentTypes());
    const [hasColorScanPositions, setHasColorScanPositions] = useState<boolean>(facilityHasColorScanPositions());
    const [hasBlackAndWhiteScanPositions, setHasBlackAndWhiteScanPositions] = useState<boolean>(facilityHasBlackAndWhiteScanPositions());
    const [has360Images, setHas360Images] = useState<boolean>(facilityHas360Images());
    const [assetTagClassOptions, setAssetTagClassOptions] = useState(() => getAssetTagClassOptions(assetSets));
    const [tagOptionsOpen, setTagOptionsOpen] = useState(false);
    const [scanOptionsOpen, setScanOptionsOpen] = useState(false);
    const [displayObstructedTags, setDisplayObstructedTags] = useState<boolean>(ViewerStore.getDisplayObstructedTags());
    const [visibleAssetTagClasses, setVisibleAssetTagClasses] = useState<string[] | null>(ViewerStore.getVisibleAssetTagClasses());
    const [layers, setLayers] = useState(ViewerStore.getContentLayers());
    const mountedRef = React.useRef(false);

    const onChange = React.useCallback(() => {
        if (!mountedRef.current) return;

        setSelectedOntologyTypes(FacilityStore.getSelectedOntologyPartContentTypes());
        setHasColorScanPositions(facilityHasColorScanPositions());
        setHasBlackAndWhiteScanPositions(facilityHasBlackAndWhiteScanPositions());
        setHas360Images(facilityHas360Images());
        setAssetTagClassOptions(getAssetTagClassOptions(assetSets));
    }, [assetSets]);

    const onViewerStoreChange = React.useCallback(() => {
        if (!mountedRef.current) return;

        setDisplayObstructedTags(ViewerStore.getDisplayObstructedTags());
        setVisibleAssetTagClasses(ViewerStore.getVisibleAssetTagClasses());
        setLayers(ViewerStore.getContentLayers());
        setAssetTagClassOptions(getAssetTagClassOptions(assetSets));
    }, [assetSets]);

    useEffect(() => {
        setAssetTagClassOptions(getAssetTagClassOptions(assetSets));
    }, [assetSets]);

    useEffect(() => {
        mountedRef.current = true;
        FacilityStore.addChangeListener(onChange);
        ViewerStore.addChangeListener(onViewerStoreChange);
        KnowledgeBookStore.addChangeListener(onViewerStoreChange);
        // The Knowledge View tag fallback lives in the Facility-wide collection, which can
        // arrive after this button first renders.
        FacilityAssetSetStore.addChangeListener(onViewerStoreChange);

        return () => {
            mountedRef.current = false;
            FacilityStore.removeChangeListener(onChange);
            ViewerStore.removeChangeListener(onViewerStoreChange);
            KnowledgeBookStore.removeChangeListener(onViewerStoreChange);
            FacilityAssetSetStore.removeChangeListener(onViewerStoreChange);
        };
    }, [onChange, onViewerStoreChange]);

    const allowedCoreKeys = React.useMemo(() => {
        return ViewerActions.getCoreKeys(selectedOntologyTypes);
    }, [selectedOntologyTypes]);

    const handleAssetsLayerToggle = () => {
        // Combined current state of "Assets/Mesh" layer
        const current = layers.assets || layers.mesh;
        const newValue = !current; // what both should be after the click

        if (layers.assets !== newValue) {
            ViewerActions.toggleLayer('assets');
        }

        if (layers.mesh !== newValue) {
            ViewerActions.toggleLayer('mesh');
        }

        /*
        // Only toggle if the layer actually needs to change
        if (allowedCoreKeys.includes('assets') && layers.assets !== newValue) {
            ViewerActions.toggleLayer('assets');
        }

        if (allowedCoreKeys.includes('mesh') && layers.mesh !== newValue) {
            ViewerActions.toggleLayer('mesh');
        }
        */
    };

    const toggleLayer = (key: keyof typeof layers) => {
        ViewerActions.toggleLayer(key);
    };
    const isAssetsMeshOn = layers.assets || layers.mesh;
    const hasPositionLayers = hasColorScanPositions || hasBlackAndWhiteScanPositions || has360Images;
    const isPositionLayerOn = layers.positions360;
    const showPositionOptions = isPositionLayerOn;
    const isPanoViewer = ViewerStore.isPanoViewerVisible();
    const isPanoKnowledgeView = isPanoViewer && !!ViewerStore.getSelectedKnowledgeView();
    const showPositionLayerControl = hasPositionLayers && (positionsOnly || !isPanoKnowledgeView);
    const showAssetTagOptions = layers.assetTags && assetTagClassOptions.length > 0;
    const positionsTitle = InovxLoader.t('facility:menu.positions360');

    useEffect(() => {
        if (!showAssetTagOptions && tagOptionsOpen) {
            setTagOptionsOpen(false);
        }
    }, [showAssetTagOptions, tagOptionsOpen]);

    useEffect(() => {
        if (!showPositionLayerControl && scanOptionsOpen) {
            setScanOptionsOpen(false);
        }
    }, [showPositionLayerControl, scanOptionsOpen]);

    const handleTagOptionsToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setScanOptionsOpen(false);
        setTagOptionsOpen(current => !current);
    };
    const handleScanOptionsToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setTagOptionsOpen(false);
        setScanOptionsOpen(current => !current);
    };
    const handlePlaceholderOptionClick = (event: React.MouseEvent) => {
        event.stopPropagation();
    };
    const handlePositionLayerToggle = () => {
        if (isPositionLayerOn) {
            setScanOptionsOpen(false);
        }

        toggleLayer('positions360');
    };
    const handleAssetTagsLayerToggle = () => {
        if (layers.assetTags) {
            setTagOptionsOpen(false);
        }

        toggleLayer('assetTags');
    };
    const isAssetTagClassVisible = (assetClassID: string) => {
        return !visibleAssetTagClasses || visibleAssetTagClasses.includes(assetClassID);
    };
    const handleDisplayObstructedTagsToggle = (event: React.MouseEvent) => {
        event.stopPropagation();
        ViewerActions.setDisplayObstructedTags(!displayObstructedTags);
    };
    const handleAssetTagClassToggle = (event: React.ChangeEvent<HTMLInputElement>, assetClassID: string) => {
        event.stopPropagation();
        ViewerActions.toggleVisibleAssetTagClass(assetClassID, assetTagClassOptions.map(option => option.id));
    };
    const handlePositionTypeToggle = (event: React.ChangeEvent<HTMLInputElement>, key: 'colorScanPositions' | 'blackAndWhiteScanPositions' | 'imagePositions') => {
        event.stopPropagation();
        toggleLayer(key);
    };

    return (
        <div id="content-layer-button-container">
            <button id="content-layer-button" title={`${InovxLoader.t('facility:menu.contentLayers')}`} className="content-layer-button">
                <span className="content-layer-icons speeddial-icon-layers"></span>
            </button>
            <div className="content-layer-button" id="content-layer-button_popup">
                <ul id="content-layer-button_ul">
                    {!positionsOnly && !isPanoViewer && (allowedCoreKeys.includes('assets') || allowedCoreKeys.includes('mesh')) && (<li className={`${isAssetsMeshOn ? 'selected' : ''}`} title={`${InovxLoader.t('facility:menu.assets')}`} onClick={handleAssetsLayerToggle}>
                        <span className="content-layer-icons speeddial-icon-assets"></span>
                    </li>)}
                    {!positionsOnly && !isPanoViewer && allowedCoreKeys.includes('cad') && (<li className={`${layers.cad ? 'selected' : ''}`} title={`${InovxLoader.t('facility:menu.cad')}`} onClick={() => toggleLayer('cad')}>
                        <span className="content-layer-icons speeddial-icon-cad"></span>
                    </li>)}
                    {!positionsOnly && !isPanoViewer && allowedCoreKeys.includes('pointCloud') && (<li className={`${layers.pointCloud ? 'selected' : ''}`} title={`${InovxLoader.t('facility:menu.pointClouds')}`} onClick={() => toggleLayer('pointCloud')}>
                        <span className="content-layer-icons speeddial-icon-point-clouds"></span>
                    </li>)}
                    {!positionsOnly && !isPanoViewer && <li className={`${layers.assemblies ? 'selected' : ''}`} title={`${InovxLoader.t('facility:menu.assemblies')}`} onClick={() => toggleLayer('assemblies')}>
                        <span className="content-layer-icons speeddial-icon-assemblies"></span>
                    </li>}
                    {showPositionLayerControl && <li className={`content-layer-scan-item ${isPositionLayerOn ? 'selected' : ''} ${scanOptionsOpen ? 'options-open' : ''}`} title={positionsTitle} onClick={handlePositionLayerToggle}>
                        <span className="content-layer-icons speeddial-icon-scan-positions"></span>
                        {showPositionOptions && (
                            <button
                                type="button"
                                className="content-layer-tag-options-button"
                                title={positionsTitle}
                                aria-label={positionsTitle}
                                onClick={handleScanOptionsToggle}
                            >
                                <span className="content-layer-tag-options-chevron"></span>
                            </button>
                        )}
                        {scanOptionsOpen && (
                            <div className="content-layer-tag-options-panel content-layer-scan-options-panel" onClick={handlePlaceholderOptionClick}>
                                <div className="content-layer-tag-options-header">
                                    <span>{positionsTitle}</span>
                                    <button
                                        type="button"
                                        className="asset-tag-tool-bar-item enabled-btn panel-close-icon content-layer-options-close-button"
                                        title={InovxLoader.t('common:header.close')}
                                        aria-label={InovxLoader.t('common:header.close')}
                                        onClick={handleScanOptionsToggle}
                                    ></button>
                                </div>
                                <div className="content-layer-tag-class-list">
                                    {hasColorScanPositions && (
                                        <label className="content-layer-tag-class-option">
                                            <input type="checkbox" checked={layers.colorScanPositions} onChange={(event) => handlePositionTypeToggle(event, 'colorScanPositions')} />
                                            <span className="content-layer-tag-class-name">{InovxLoader.t('facility:menu.colorPanos')}</span>
                                        </label>
                                    )}
                                    {hasBlackAndWhiteScanPositions && (
                                        <label className="content-layer-tag-class-option">
                                            <input type="checkbox" checked={layers.blackAndWhiteScanPositions} onChange={(event) => handlePositionTypeToggle(event, 'blackAndWhiteScanPositions')} />
                                            <span className="content-layer-tag-class-name">{InovxLoader.t('facility:menu.blackAndWhitePanos')}</span>
                                        </label>
                                    )}
                                    {has360Images && (
                                        <label className="content-layer-tag-class-option">
                                            <input type="checkbox" checked={layers.imagePositions} onChange={(event) => handlePositionTypeToggle(event, 'imagePositions')} />
                                            <span className="content-layer-tag-class-name">{InovxLoader.t('facility:menu.image360')}</span>
                                        </label>
                                    )}
                                </div>
                            </div>
                        )}
                    </li>}
                    {!positionsOnly && <li className={`content-layer-tag-item ${layers.assetTags ? 'selected' : ''} ${showAssetTagOptions ? 'has-options' : ''} ${tagOptionsOpen ? 'options-open' : ''}`} title={`${InovxLoader.t('facility:menu.assetTags')}`} onClick={handleAssetTagsLayerToggle}>
                        <span className="content-layer-icons speeddial-icon-asset-tags"></span>
                        {showAssetTagOptions && (
                            <button
                                type="button"
                                className="content-layer-tag-options-button"
                                title={`${InovxLoader.t('facility:menu.assetTags')}`}
                                aria-label={`${InovxLoader.t('facility:menu.assetTags')}`}
                                onClick={handleTagOptionsToggle}
                            >
                                <span className="content-layer-tag-options-chevron"></span>
                            </button>
                        )}
                        {showAssetTagOptions && tagOptionsOpen && (
                            <div className="content-layer-tag-options-panel" onClick={handlePlaceholderOptionClick}>
                                <div className="content-layer-tag-options-header">
                                    <span>{InovxLoader.t('facility:menu.assetTags')}</span>
                                    <button
                                        type="button"
                                        className="asset-tag-tool-bar-item enabled-btn panel-close-icon content-layer-options-close-button"
                                        title={InovxLoader.t('common:header.close')}
                                        aria-label={InovxLoader.t('common:header.close')}
                                        onClick={handleTagOptionsToggle}
                                    ></button>
                                </div>
                                <div className="content-layer-tag-option-row" onClick={handleDisplayObstructedTagsToggle}>
                                    <span>{InovxLoader.t('facility:action.displayObstructedTags')}</span>
                                    <span className={`content-layer-toggle-preview ${displayObstructedTags ? 'on' : ''}`}>
                                        <span></span>
                                    </span>
                                </div>
                                <div className="content-layer-tag-options-section">
                                    <div className="content-layer-tag-options-section-header">
                                        <span>{InovxLoader.t('facility:queryBuilder.assetClass')}</span>
                                    </div>
                                    <div className="content-layer-tag-class-list">
                                        {assetTagClassOptions.map(option => (
                                            <label className="content-layer-tag-class-option" key={option.id}>
                                                <input type="checkbox" checked={isAssetTagClassVisible(option.id)} onChange={(event) => handleAssetTagClassToggle(event, option.id)} />
                                                <span className="content-layer-tag-class-name">{option.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </li>}
                </ul>

            </div>
        </div>
    );
};

export default ContentLayersButton;
